import React, { useRef, useState } from "react";
import { jsPDF } from "jspdf";

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 23, 42, 0.55)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 16,
  zIndex: 9999,
};

const modalStyle = {
  width: "min(1000px, 96vw)",
  maxHeight: "92vh",
  overflow: "auto",
  background: "#fff",
  borderRadius: 18,
  padding: 18,
  boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
  display: "grid",
  gap: 16,
};

const buttonStyle = {
  border: "1px solid #cbd5e1",
  borderRadius: 10,
  padding: "10px 14px",
  background: "#fff",
  cursor: "pointer",
  textDecoration: "none",
};

const primaryButtonStyle = {
  ...buttonStyle,
  background: "#0f172a",
  color: "#fff",
  borderColor: "#0f172a",
};

const dangerButtonStyle = {
  ...buttonStyle,
  color: "#b91c1c",
  borderColor: "#fecaca",
  background: "#fff",
};

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function dataUrlToBlob(dataUrl) {
  const parts = dataUrl.split(",");
  const mimeMatch = parts[0].match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : "image/jpeg";
  const binary = atob(parts[1]);
  const len = binary.length;
  const bytes = new Uint8Array(len);

  for (let i = 0; i < len; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }

  return new Blob([bytes], { type: mime });
}

async function normalizeImageForPdf(dataUrl) {
  const img = await loadImage(dataUrl);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  const maxDimension = 2200;
  let { width, height } = img;

  if (width > height && width > maxDimension) {
    height = Math.round((height * maxDimension) / width);
    width = maxDimension;
  } else if (height >= width && height > maxDimension) {
    width = Math.round((width * maxDimension) / height);
    height = maxDimension;
  }

  canvas.width = width;
  canvas.height = height;

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);

  return canvas.toDataURL("image/jpeg", 0.9);
}

export default function ScanDocumentModal({
  open,
  onClose,
  onComplete,
  uploading = false,
}) {
  const fileInputRef = useRef(null);
  const [pages, setPages] = useState([]);
  const [fileName, setFileName] = useState("Scanned Document");
  const [buildingPdf, setBuildingPdf] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  async function handleFilesSelected(event) {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    setError("");

    try {
      const nextPages = await Promise.all(
        files.map(async (file, index) => {
          const originalUrl = await readFileAsDataUrl(file);
          const normalizedUrl = await normalizeImageForPdf(originalUrl);

          return {
            id: `${Date.now()}-${index}-${Math.random().toString(36).slice(2)}`,
            name: file.name || `Page ${index + 1}`,
            previewUrl: normalizedUrl,
          };
        })
      );

      setPages((prev) => [...prev, ...nextPages]);
    } catch (err) {
      console.error("Error reading scan images:", err);
      setError("Could not load selected images.");
    } finally {
      event.target.value = "";
    }
  }

  function movePage(index, direction) {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= pages.length) return;

    setPages((prev) => {
      const next = [...prev];
      const temp = next[index];
      next[index] = next[nextIndex];
      next[nextIndex] = temp;
      return next;
    });
  }

  function removePage(index) {
    setPages((prev) => prev.filter((_, i) => i !== index));
  }

  async function handleCreatePdf() {
    if (!pages.length || buildingPdf || uploading) return;

    setBuildingPdf(true);
    setError("");

    try {
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "pt",
        format: "a4",
        compress: true,
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 24;

      for (let i = 0; i < pages.length; i += 1) {
        const page = pages[i];
        const img = await loadImage(page.previewUrl);

        if (i > 0) {
          pdf.addPage();
        }

        const usableWidth = pageWidth - margin * 2;
        const usableHeight = pageHeight - margin * 2;

        const widthRatio = usableWidth / img.width;
        const heightRatio = usableHeight / img.height;
        const scale = Math.min(widthRatio, heightRatio);

        const renderWidth = img.width * scale;
        const renderHeight = img.height * scale;
        const x = (pageWidth - renderWidth) / 2;
        const y = (pageHeight - renderHeight) / 2;

        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, pageWidth, pageHeight, "F");
        pdf.addImage(page.previewUrl, "JPEG", x, y, renderWidth, renderHeight);
      }

      const blob = pdf.output("blob");
      const pdfFile = new File(
        [blob],
        `${(fileName || "Scanned Document").trim()}.pdf`,
        { type: "application/pdf" }
      );

      await onComplete(pdfFile);

      setPages([]);
      setFileName("Scanned Document");
      onClose();
    } catch (err) {
      console.error("Error creating scanned PDF:", err);
      setError("Could not create PDF from scanned pages.");
    } finally {
      setBuildingPdf(false);
    }
  }

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <div>
            <div style={{ fontSize: 22, fontWeight: 800 }}>Scan Document</div>
            <div style={{ color: "#64748b", marginTop: 4 }}>
              Add one or more pages, reorder them, then save as a single PDF.
            </div>
          </div>

          <button type="button" style={buttonStyle} onClick={onClose}>
            Close
          </button>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(220px, 280px) 1fr",
            gap: 16,
          }}
        >
          <div style={{ display: "grid", gap: 12, alignContent: "start" }}>
            <label style={{ display: "grid", gap: 6 }}>
              <span style={{ fontSize: 14, color: "#334155" }}>PDF Name</span>
              <input
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                style={{
                  width: "100%",
                  border: "1px solid #cbd5e1",
                  borderRadius: 10,
                  padding: "10px 12px",
                  fontSize: 14,
                  boxSizing: "border-box",
                }}
              />
            </label>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              style={{ display: "none" }}
              onChange={handleFilesSelected}
            />

            <button
              type="button"
              style={buttonStyle}
              onClick={() => fileInputRef.current?.click()}
            >
              Add Pages
            </button>

            <button
              type="button"
              style={primaryButtonStyle}
              disabled={!pages.length || buildingPdf || uploading}
              onClick={handleCreatePdf}
            >
              {buildingPdf || uploading ? "Saving..." : "Create PDF and Upload"}
            </button>

            {error ? <div style={{ color: "#b91c1c" }}>{error}</div> : null}

            <div style={{ color: "#64748b", fontSize: 13 }}>
              Tips:
              <br />
              • Use one photo per page
              <br />
              • Keep the document flat
              <br />
              • Good lighting helps
            </div>
          </div>

          <div>
            {!pages.length ? (
              <div
                style={{
                  border: "1px dashed #cbd5e1",
                  borderRadius: 14,
                  minHeight: 300,
                  display: "grid",
                  placeItems: "center",
                  color: "#64748b",
                  background: "#f8fafc",
                  padding: 20,
                  textAlign: "center",
                }}
              >
                No pages added yet.
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                  gap: 12,
                }}
              >
                {pages.map((page, index) => (
                  <div
                    key={page.id}
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 14,
                      padding: 10,
                      background: "#fff",
                      display: "grid",
                      gap: 8,
                    }}
                  >
                    <div style={{ fontWeight: 700 }}>Page {index + 1}</div>

                    <div
                      style={{
                        borderRadius: 10,
                        overflow: "hidden",
                        border: "1px solid #e5e7eb",
                        background: "#f8fafc",
                        aspectRatio: "3 / 4",
                      }}
                    >
                      <img
                        src={page.previewUrl}
                        alt={`Page ${index + 1}`}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          display: "block",
                        }}
                      />
                    </div>

                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <button
                        type="button"
                        style={buttonStyle}
                        onClick={() => movePage(index, -1)}
                        disabled={index === 0}
                      >
                        Up
                      </button>
                      <button
                        type="button"
                        style={buttonStyle}
                        onClick={() => movePage(index, 1)}
                        disabled={index === pages.length - 1}
                      >
                        Down
                      </button>
                      <button
                        type="button"
                        style={dangerButtonStyle}
                        onClick={() => removePage(index)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}